<template>
  <div class="survey-app-container">
    <div class="glass-header mb-4 d-flex justify-content-between align-items-center">
      <div>
        <h2 class="mb-0 fw-bold text-dark">Survey Management</h2>
        <p v-if="!isHost" class="text-muted mb-0">Create and manage surveys to collect guests feedback</p>
        <p v-else class="text-muted mb-0">Select a survey below to start collecting feedback</p>
      </div>
      <div class="d-flex ms-auto">
        <button v-if="offlineResponsesCount > 0" class="btn btn-warning px-4 py-2 rounded-pill shadow-sm fw-bold me-2"
          @click="syncOfflineResponses">
          <i class="fa fa-sync me-2"></i> Sync {{ offlineResponsesCount }} Offline
        </button>
        <button v-if="!isHost" class="btn btn-primary-gradient px-4 py-2 rounded-pill shadow-sm fw-bold"
          @click="createNewSurvey">
          <i class="fa fa-plus me-2"></i> Create Survey
        </button>
      </div>
    </div>

    <!-- Admin View -->
    <div v-if="!isHost">
      <!-- KPI Cards -->
      <div class="row mb-5">
        <div class="col-md-3">
          <div class="kpi-card glass-panel">
            <div class="kpi-title">Total Surveys</div>
            <div class="kpi-value">{{ surveys.length < 10 ? '0' + surveys.length : surveys.length }}</div>
            </div>
          </div>
          <div class="col-md-3">
            <div class="kpi-card glass-panel">
              <div class="kpi-title">Active Surveys</div>
              <div class="kpi-value">{{ activeSurveys.length < 10 ? '0' + activeSurveys.length : activeSurveys.length
              }}</div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="kpi-card glass-panel">
                <div class="kpi-title">Total Responses</div>
                <div class="kpi-value">{{ totalResponsesCount < 10 ? '0' + totalResponsesCount : totalResponsesCount
                }}</div>
                </div>
              </div>
              <div class="col-md-3">
                <div class="kpi-card glass-panel">
                  <div class="kpi-title">Avg. Responses / Survey</div>
                  <div class="kpi-value">{{ avgResponseRate }}</div>
                </div>
              </div>
            </div>

            <!-- Survey List -->
            <div class="survey-list">
              <div v-if="surveys.length === 0" class="empty-state glass-panel text-center p-5">
                <h4 class="text-muted">No surveys created yet.</h4>
                <button class="btn btn-primary-gradient mt-3" @click="createNewSurvey">Create your first survey</button>
              </div>

              <div v-for="survey in surveys" :key="survey.id"
                class="survey-item glass-panel mb-3 p-4 d-flex justify-content-between align-items-center border-0 shadow-sm rounded-3">
                <div>
                  <h5 class="fw-bold mb-1 text-dark" style="font-size: 1.15rem;">{{ survey.attributes.title }}</h5>
                  <p class="text-muted mb-3" style="font-size: 0.95rem;">{{ survey.attributes.description }}</p>
                  <div class="d-flex align-items-center text-muted" style="font-size: 0.9rem;">
                    <span class="me-4"><i class="fa fa-list-ul"></i>&nbsp;&nbsp;{{ survey.attributes.questions?.length
                      || 0 }} questions</span>
                    <span><i class="fa fa-calendar"></i>&nbsp;&nbsp;Created: {{ new
                      Date(survey.attributes.createdAt).toLocaleDateString() }}</span>
                  </div>
                </div>

                <div class="d-flex align-items-center">
                  <div class="d-flex align-items-center me-4">
                    <input class="tc app-checkbox me-2" type="checkbox"
                      :checked="survey.attributes.publishedAt !== null" @change="togglePublish(survey)"
                      :id="'pub-' + survey.id">
                    <label class="text-muted fw-medium mb-0" style="cursor: pointer; font-size: 0.95rem;"
                      :for="'pub-' + survey.id">
                      {{ survey.attributes.publishedAt ? 'Active' : 'Draft' }}
                    </label>
                  </div>

                  <div class="d-flex gap-2">
                    <button class="btn btn-light btn-sm rounded-pill text-primary px-3 fw-bold" title="Edit Survey"
                      @click="editSurvey(survey.id)">
                      <i class="fa fa-pen"></i> Edit
                    </button>
                    <button class="btn btn-light btn-sm rounded-pill text-success px-3 fw-bold" title="View Responses"
                      @click="viewResponses(survey.id)">
                      <i class="fa fa-file-alt"></i> View
                    </button>
                    <button class="btn btn-light btn-sm rounded-pill text-info px-3 fw-bold"
                      title="Fill Survey (Preview)" @click="fillSurvey(survey.id)">
                      <i class="fa fa-external-link-alt"></i> Preview
                    </button>
                    <button class="btn btn-light btn-sm rounded-pill text-warning px-3 fw-bold" title="Duplicate Survey"
                      @click="duplicateSurvey(survey)">
                      <i class="fa fa-copy"></i> Duplicate
                    </button>
                    <button class="btn btn-light btn-sm rounded-pill text-danger px-3 fw-bold" title="Delete Survey"
                      @click="deleteSurvey(survey.id)">
                      <i class="fa fa-trash"></i> Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Host View -->
          <div v-else>
            <div v-if="activeSurveys.length === 0"
              class="empty-state glass-panel text-center p-5 mt-4 border-0 shadow-sm">
              <i class="fa fa-folder-open text-muted mb-3" style="font-size: 3rem; opacity: 0.5;"></i>
              <h4 class="text-muted">No surveys currently available.</h4>
              <p class="text-muted mb-0">Please check back later.</p>
            </div>

            <div class="row mt-4">
              <div v-for="survey in activeSurveys" :key="survey.id" class="col-md-4 mb-4">
                <div class="glass-panel p-4 h-100 d-flex flex-column tc app-host-card border-0 shadow-sm">
                  <h4 class="fw-bold text-dark mb-2">{{ survey.attributes.title }}</h4>
                  <p class="text-muted flex-grow-1">{{ survey.attributes.description }}</p>
                  <div class="text-muted small mb-4 fw-medium">
                    <i class="fa fa-list-ul me-1"></i> {{ survey.attributes.questions?.length || 0 }} Questions
                  </div>
                  <button class="btn btn-primary-gradient w-100 rounded-pill py-2 fw-bold"
                    @click="fillSurvey(survey.id)">
                    Start Survey <i class="fa fa-arrow-right ms-2"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

        </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'SurveyList',
  data() {
    return {
      surveys: [],
      totalResponsesCount: 0,
      isHost: false,
      offlineResponsesCount: 0
    };
  },
  computed: {
    activeSurveys() {
      return this.surveys.filter(s => s.attributes.publishedAt);
    },
    avgResponseRate() {
      if (this.activeSurveys.length === 0) return '0%';
      const rate = (this.totalResponsesCount / this.activeSurveys.length).toFixed(1);
      return `${rate}`;
    }
  },
  async mounted() {
    const userProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
    // Determine if the user is a host. If they don't have Admin/Authenticated role, treat as Host.
    this.isHost = userProfile.role && userProfile.role.name === 'Host';

    await this.fetchSurveys();
    if (!this.isHost) {
      await this.fetchResponses();
    }
    this.checkOfflineResponses();
    window.addEventListener('online', this.syncOfflineResponses);
  },
  beforeDestroy() {
    window.removeEventListener('online', this.syncOfflineResponses);
  },
  methods: {
    async fetchSurveys() {
      try {
        const token = localStorage.getItem('jwt');
        const baseUrl = process.env.VUE_APP_BASE_URL || 'http://localhost:1337';
        // Hosts only need active surveys, but admins need all. publicationState=preview fetches both.
        const res = await axios.get(`${baseUrl}/api/surveys?populate=questions&publicationState=preview`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        this.surveys = res.data.data;
        
        // Cache for offline mode
        localStorage.setItem('cached_surveys', JSON.stringify(this.surveys));
        this.surveys.forEach(survey => {
          localStorage.setItem(`survey_${survey.id}`, JSON.stringify(survey));
        });
      } catch (e) {
        console.error("Failed to load surveys", e);
        const cached = localStorage.getItem('cached_surveys');
        if (cached) {
          this.surveys = JSON.parse(cached);
          this.$bvToast.toast('Loaded cached surveys (Offline Mode)', { title: 'Offline', variant: 'info', solid: true });
        }
      }
    },
    async fetchResponses() {
      try {
        const token = localStorage.getItem('jwt');
        const baseUrl = process.env.VUE_APP_BASE_URL || 'http://localhost:1337';
        const res = await axios.get(`${baseUrl}/api/survey-responses`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        this.totalResponsesCount = res.data.meta.pagination.total;
      } catch (e) {
        console.error("Failed to load responses", e);
      }
    },
    createNewSurvey() {
      this.$router.push('/survey-management/builder');
    },
    editSurvey(id) {
      this.$router.push(`/survey-management/builder/${id}`);
    },
    fillSurvey(id) {
      this.$router.push(`/survey-management/fill/${id}`);
    },
    viewResponses(id) {
      this.$router.push(`/survey-management/responses/${id}`);
    },
    async togglePublish(survey) {
      try {
        const token = localStorage.getItem('jwt');
        const baseUrl = process.env.VUE_APP_BASE_URL || 'http://localhost:1337';
        const isPublished = survey.attributes.publishedAt !== null;

        if (isPublished) {
          await axios.put(`${baseUrl}/api/surveys/${survey.id}`, { data: { publishedAt: null } }, { headers: { Authorization: `Bearer ${token}` } });
        } else {
          await axios.put(`${baseUrl}/api/surveys/${survey.id}`, { data: { publishedAt: new Date().toISOString() } }, { headers: { Authorization: `Bearer ${token}` } });
        }
        await this.fetchSurveys();
        this.$bvToast.toast(`Survey is now ${isPublished ? 'Draft' : 'Active'}`, { title: 'Success', variant: 'success', solid: true });
      } catch (e) {
        console.error(e);
        this.$bvToast.toast('Failed to change publish state', { title: 'Error', variant: 'danger', solid: true });
      }
    },
    async deleteSurvey(id) {
      this.$bvModal.msgBoxConfirm("Are you sure you want to delete this survey?", {
        title: 'Delete Survey',
        okVariant: 'danger',
        okTitle: 'Delete',
        cancelTitle: 'Cancel',
        centered: true
      }).then(async value => {
        if (value) {
          try {
            const token = localStorage.getItem('jwt');
            const baseUrl = process.env.VUE_APP_BASE_URL || 'http://localhost:1337';
            await axios.delete(`${baseUrl}/api/surveys/${id}`, { headers: { Authorization: `Bearer ${token}` } });
            await this.fetchSurveys();
            this.$bvToast.toast('Survey deleted successfully', { title: 'Success', variant: 'success', solid: true });
          } catch (e) {
            console.error(e);
            this.$bvToast.toast('Failed to delete survey', { title: 'Error', variant: 'danger', solid: true });
          }
        }
      });
    },
    async duplicateSurvey(survey) {
      try {
        const token = localStorage.getItem('jwt');
        const baseUrl = process.env.VUE_APP_BASE_URL || 'http://localhost:1337';
        
        const newQuestions = (survey.attributes.questions || []).map(q => {
          const { id, ...rest } = q;
          return rest;
        });

        const newSurveyData = {
          title: survey.attributes.title + ' (Copy)',
          description: survey.attributes.description,
          publishedAt: null, // Draft by default
          questions: newQuestions
        };

        await axios.post(`${baseUrl}/api/surveys`, { data: newSurveyData }, { headers: { Authorization: `Bearer ${token}` } });
        await this.fetchSurveys();
        this.$bvToast.toast('Survey duplicated successfully', { title: 'Success', variant: 'success', solid: true });
      } catch (e) {
        console.error(e);
        this.$bvToast.toast('Failed to duplicate survey', { title: 'Error', variant: 'danger', solid: true });
      }
    },
    getOfflineStorageKey() {
      const userProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
      const userId = userProfile.id || 'anonymous';
      return `offline_survey_responses_${userId}`;
    },
    checkOfflineResponses() {
      const key = this.getOfflineStorageKey();
      const offline = JSON.parse(localStorage.getItem(key) || '[]');
      this.offlineResponsesCount = offline.length;
    },
    async syncOfflineResponses() {
      const key = this.getOfflineStorageKey();
      const offline = JSON.parse(localStorage.getItem(key) || '[]');
      if (offline.length === 0) return;

      this.$bvToast.toast(`Syncing ${offline.length} offline responses...`, {
        title: 'Syncing',
        variant: 'info',
        solid: true
      });

      const token = localStorage.getItem('jwt');
      const baseUrl = process.env.VUE_APP_BASE_URL || 'http://localhost:1337';
      
      let successCount = 0;
      for (const payload of offline) {
        try {
          await axios.post(`${baseUrl}/api/survey-responses`, payload, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
          successCount++;
        } catch (e) {
          console.error('Failed to sync response', e);
        }
      }

      if (successCount === offline.length) {
        localStorage.removeItem(key);
        this.offlineResponsesCount = 0;
        this.$bvToast.toast('All offline responses synced successfully!', {
          title: 'Sync Complete',
          variant: 'success',
          solid: true
        });
        if (!this.isHost) this.fetchResponses();
      } else {
        const remaining = offline.slice(successCount);
        localStorage.setItem(key, JSON.stringify(remaining));
        this.offlineResponsesCount = remaining.length;
        this.$bvToast.toast(`Synced ${successCount} responses. Some failed.`, {
          title: 'Sync Partial',
          variant: 'warning',
          solid: true
        });
      }
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap');

.survey-app-container {
  font-family: 'Outfit', sans-serif;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  background-color: #F8F9FC;
  min-height: 100vh;
}

.text-gradient {
  background: linear-gradient(135deg, #6C5DD3, #8a7df0);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.btn-primary-gradient {
  background: #6C5DD3;
  border: none;
  color: white;
  font-weight: 600;
  box-shadow: 0 4px 14px 0 rgba(108, 93, 211, 0.3);
  transition: transform 0.2s, box-shadow 0.2s;
}

.btn-primary-gradient:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(108, 93, 211, 0.4);
  color: white;
}

.glass-panel {
  background: #ffffff;
  border: 1px solid #E2E2EA;
  border-radius: 12px;
}

.kpi-card {
  padding: 1.5rem;
  height: 100%;
}

.kpi-title {
  color: #B2B2BF;
  font-size: 0.95rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
}

.kpi-value {
  font-size: 2.5rem;
  font-weight: 700;
  color: #11142D;
  line-height: 1;
}

.survey-item {
  transition: transform 0.2s, box-shadow 0.2s;
}

.survey-item:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.03) !important;
}

.tc app-btn-icon {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background-color: #F8F9FC;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  transition: all 0.2s;
  padding: 0;
}

.tc app-btn-icon:hover {
  background-color: #F0EDFF;
  border-color: #E2E2EA;
}

.tc app-checkbox {
  width: 1.4rem;
  height: 1.4rem;
  border-color: #B2B2BF;
  border-radius: 4px;
  cursor: pointer;
}

.tc app-checkbox:checked {
  background-color: #6C5DD3;
  border-color: #6C5DD3;
}

.tc app-host-card {
  border-radius: 16px;
  transition: transform 0.2s, box-shadow 0.2s;
}

.tc app-host-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.06) !important;
}
</style>
